import {
  useHttpClient,
  useNextSortOrder,
  useToast,
} from '@airship-platform/airship-patterns';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import {
  AppointmentProposalOptionSOW,
  AppointmentProposalOptionSOWCreate,
  AppointmentProposalOptionSOWUpdate,
} from './appointmentProposalOptionSows.models';
import {
  initState,
  optionSOWReducer,
} from './appointmentProposalOptionSows.reducer';

type CreatePayload = AppointmentProposalOptionSOWCreate;
type UpdatePayload = AppointmentProposalOptionSOWUpdate;

interface OfflineAppointmentProposalOptionSOWContextProps {
  appendSOWs: (sows: AppointmentProposalOptionSOW[]) => void;
  upsertSOWs: (sows: AppointmentProposalOptionSOW[]) => void;
  selectProposalSOWs: (appointmentProposalId: string) => void;
  setSOWsMutating: (opts: { creatingIds?: string[] }) => void;
  clearSOWsMutating: (opts: { creatingIds?: string[]; error?: string }) => void;
}

interface OptionsSOWContextProps
  extends OfflineAppointmentProposalOptionSOWContextProps {
  proposalSows: AppointmentProposalOptionSOW[];
  isLoading: boolean;
  isLoaded: boolean;
  isLoadingSOWIds: string[];
  loadedProposalIds: string[];
  error: string | null;
  fetchAppointmentProposalSOWs: (
    appointmentId: string,
    appointmentProposalId: string
  ) => Promise<AppointmentProposalOptionSOW[]>;
  createAppointmentProposalOptionSOW: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    data: CreatePayload
  ) => Promise<AppointmentProposalOptionSOW>;
  createManyAppointmentProposalOptionSOWs: (
    appointmentId: string,
    appointmentProposalId: string,
    payloads: {
      appointmentProposalOptionId: string;
      sow: CreatePayload;
    }[]
  ) => Promise<AppointmentProposalOptionSOW[]>;
  updateAppointmentProposalOptionSOW: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionSOWId: string,
    data: UpdatePayload
  ) => Promise<AppointmentProposalOptionSOW>;
  deleteAppointmentProposalOptionSOW: (
    appointmentId: string,
    appointmentProposalId: string,
    appointmentProposalOptionId: string,
    appointmentProposalOptionSOWId: string
  ) => Promise<void>;
  getNextSortOrder: (offset?: number) => number;
}

export const AppointmentProposalOptionSOWsContext = createContext<
  OptionsSOWContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren;

export const AppointmentProposalOptionSOWsProvider: React.FC<OwnProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(optionSOWReducer, initState);
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const proposalSows = useMemo(
    () =>
      state.ids
        .map((id) => state.entities[id])
        .filter(
          (a) =>
            a.appointmentProposalId === state.selectedProposalId &&
            a.status !== 'DELETED'
        )
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [state.entities, state.ids, state.selectedProposalId]
  );

  const fetchAppointmentProposalSOWs: OptionsSOWContextProps['fetchAppointmentProposalSOWs'] =
    useCallback(
      async (appointmentId, appointmentProposalId) => {
        dispatch({
          type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS',
          payload: { appointmentProposalId },
        });
        try {
          const optionSows = await httpClient.get<
            AppointmentProposalOptionSOW[]
          >(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/sows`
          );
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS_SUCCESS',
            payload: { optionSows, appointmentProposalId },
          });
          return optionSows;
        } catch (error) {
          dispatch({
            type: 'FETCH_APPOINTMENT_PROPOSAL_SOWS_FAILURE',
            payload: { error: `${error}` },
          });
          return [];
        }
      },
      [httpClient]
    );

  const createAppointmentProposalOptionSOWInternal = useCallback(
    async (
      appointmentId: string,
      appointmentProposalId: string,
      appointmentProposalOptionId: string,
      create: CreatePayload
    ) =>
      httpClient.post<AppointmentProposalOptionSOW>(
        `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/sows`,
        create
      ),
    [httpClient]
  );

  const createAppointmentProposalOptionSOW: OptionsSOWContextProps['createAppointmentProposalOptionSOW'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        data
      ) => {
        dispatch({
          type: 'CREATE_OPTION_SOW',
          payload: { sowId: data.sowId },
        });
        try {
          const optionSOW = await createAppointmentProposalOptionSOWInternal(
            appointmentId,
            appointmentProposalId,
            appointmentProposalOptionId,
            data
          );
          dispatch({
            type: 'CREATE_OPTION_SOW_SUCCESS',
            payload: { sow: optionSOW },
          });
          return optionSOW;
        } catch (error) {
          dispatch({
            type: 'CREATE_OPTION_SOW_FAILURE',
            payload: { error: `${error}`, sowId: data.sowId },
          });
          throw error;
        }
      },
      [createAppointmentProposalOptionSOWInternal]
    );

  const createManyAppointmentProposalOptionSOWs: OptionsSOWContextProps['createManyAppointmentProposalOptionSOWs'] =
    useCallback(
      async (appointmentId, appointmentProposalId, payloads) => {
        const sowIds = payloads.map((p) => p.sow.sowId);

        dispatch({
          type: 'CREATE_MANY_OPTION_SOW',
          payload: { sowIds },
        });

        try {
          const appointmentProposalOptionSOWs = await Promise.all(
            payloads.map(({ appointmentProposalOptionId, sow }) =>
              createAppointmentProposalOptionSOWInternal(
                appointmentId,
                appointmentProposalId,
                appointmentProposalOptionId,
                sow
              )
            )
          );

          dispatch({
            type: 'CREATE_MANY_OPTION_SOW_SUCCESS',
            payload: { sowIds, appointmentProposalOptionSOWs },
          });
          return appointmentProposalOptionSOWs;
        } catch (error) {
          dispatch({
            type: 'CREATE_MANY_OPTION_SOW_FAILURE',
            payload: { error: `${error}`, sowIds },
          });
          return [];
        }
      },
      [createAppointmentProposalOptionSOWInternal]
    );

  const updateAppointmentProposalOptionSOW: OptionsSOWContextProps['updateAppointmentProposalOptionSOW'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionSOWId,
        data
      ) => {
        dispatch({ type: 'UPDATE_OPTION_SOW' });
        try {
          const optionSOW = await httpClient.put<AppointmentProposalOptionSOW>(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/sows/${appointmentProposalOptionSOWId}`,
            data
          );
          dispatch({
            type: 'UPDATE_OPTION_SOW_SUCCESS',
            payload: { sow: optionSOW },
          });
          return optionSOW;
        } catch (error) {
          dispatch({
            type: 'UPDATE_OPTION_SOW_FAILURE',
            payload: { error: `${error}` },
          });
          throw error;
        }
      },
      [httpClient]
    );

  const deleteAppointmentProposalOptionSOW: OptionsSOWContextProps['deleteAppointmentProposalOptionSOW'] =
    useCallback(
      async (
        appointmentId,
        appointmentProposalId,
        appointmentProposalOptionId,
        appointmentProposalOptionSOWId
      ) => {
        dispatch({ type: 'DELETE_OPTION_SOW' });
        try {
          await httpClient.delete(
            `/appointments/${appointmentId}/proposals/${appointmentProposalId}/options/${appointmentProposalOptionId}/sows/${appointmentProposalOptionSOWId}`
          );
          dispatch({
            type: 'DELETE_OPTION_SOW_SUCCESS',
            payload: { appointmentProposalOptionSOWId },
          });
        } catch (error) {
          dispatch({
            type: 'DELETE_OPTION_SOW_FAILURE',
            payload: { error: `${error}` },
          });
        }
      },
      [httpClient]
    );

  const getNextSortOrder = useNextSortOrder(proposalSows);

  const appendSOWs: OptionsSOWContextProps['appendSOWs'] = useCallback(
    (appointmentProposalOptionSOWs) =>
      dispatch({
        type: 'UPSERT_SOWS',
        payload: { appointmentProposalOptionSOWs },
      }),
    []
  );

  const selectProposalSOWs: OptionsSOWContextProps['selectProposalSOWs'] =
    useCallback(
      (appointmentProposalId) =>
        dispatch({
          type: 'SELECT_PROPOSAL_SOWS',
          payload: { appointmentProposalId },
        }),
      []
    );

  const setSOWsMutating: OptionsSOWContextProps['setSOWsMutating'] =
    useCallback(
      (opts) => dispatch({ type: 'SET_SOWS_MUTATING', payload: opts }),
      []
    );

  const clearSOWsMutating: OptionsSOWContextProps['clearSOWsMutating'] =
    useCallback(
      (opts) => dispatch({ type: 'CLEAR_SOWS_MUTATING', payload: opts }),
      []
    );

  return (
    <AppointmentProposalOptionSOWsContext.Provider
      value={{
        proposalSows,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        isLoadingSOWIds: state.isLoadingSOWIds,
        loadedProposalIds: state.loadedProposalIds,
        error: state.error,
        fetchAppointmentProposalSOWs,
        createAppointmentProposalOptionSOW,
        createManyAppointmentProposalOptionSOWs,
        updateAppointmentProposalOptionSOW,
        deleteAppointmentProposalOptionSOW,
        getNextSortOrder,
        appendSOWs,
        upsertSOWs: appendSOWs,
        selectProposalSOWs,
        setSOWsMutating,
        clearSOWsMutating,
      }}
    >
      {children}
    </AppointmentProposalOptionSOWsContext.Provider>
  );
};

export const useAppointmentProposalOptionSOWs = () => {
  const context = useContext(AppointmentProposalOptionSOWsContext);
  if (!context) {
    throw new Error(
      'useAppointmentProposalOptionSOWs must be used within a AppointmentProposalOptionSOWsProvider'
    );
  }
  return context;
};
